/**
 * ConvoCard Component
 * This component renders a conversation card that displays messages, media, and interactions between users.
 * It handles both sent and received messages with different styling and functionality.
 */

import React, { FC, useState, useEffect, useRef, useCallback } from "react";
import { Image, HStack, Pressable, Box, VStack, Text, View } from "native-base";
import {
  MoreHorizontalIcon,
  TranslationIcon,
} from "../../../assets/icons/icon-assets";
import ProfilePic from "../../../components/common/ProfilePicture";
import * as AV from "../../../AVCore/AVCore";
import { ILink } from "../../../AVCore/Interfaces/Link";
import Moment from "moment";
import { Link } from "../../../AVCore/Link";
import { Link as RouterLink, useNavigate } from "react-router-dom";
import { Dimensions } from "react-native";
import { useSelector } from "react-redux";
import { Loc } from "../../../App/Loc";
import AVlinktext from "../../../components/common/AVlinktext";
import AVConverttime from "../../../components/posts/AVConverttime";
import settings from "../../../Platform/settings.json";
import GridView from "../../../components/posts/GridView";
import { Media } from "../../../AVCore/Media";
import VoiceMessage from "../../../components/common/VoiceMessage";
import { RootState } from "../../../types_interface/RootState";
import { IAVEntity } from "../../../AVCore/Interfaces/AVEntity";

// Interface for translation data

interface Translation {
  lang: string;
  text: string;
}

// Extended Post interface for conversation posts
//Includes additional properties for replies, translations, and media

interface ConvoPost extends AV.IPost {
  ReplyPost?: ConvoPost[];
  isTranslateText?: boolean;

  galleryPost?: AV.IMedia[];
}

/**
 * Props interface for ConvoCard component
 * Contains all necessary data and callback functions for the conversation card
 */
interface ConvoCardProps {
  data: ConvoPost;
  replyValue: string | undefined;
  setreplyValue: (value?: string) => void;
  profile: AV.IAVEntity;
  itemHeights: number[];
  setItemHeights: (heights: number[]) => void;
  convoPost: ConvoPost[];
  setShowPreview: (show: boolean) => void;
  setSingleImage: (image: ConvoPost) => void;
  setShowAllImage: (images: ConvoPost) => void;
  setHeader: (show: boolean) => void;
  HandleEditSet: (item: ConvoPost) => void;
  isOpen: boolean;
  setIsOpen: (open: boolean) => void;
  selectItem: ConvoPost;
  setSelectItem: (item: ConvoPost) => void;
  showFeedbackImpressions: boolean;
  setShowFeedbackImpressions: (show: boolean) => void;
  mdlCreateConvoVisible: boolean;
  setMdlCreateConvoVisible: (show: boolean) => void;
  setShowConvoListModal: (show: boolean) => void;
  setMdlConvoVisible: (show: boolean) => void;
  setConvoLoading: (loading: boolean) => void;
  setIsOpenConvo: (open: boolean) => void;
  itemEdit: ConvoPost;
  setItemEdit: (item: ConvoPost) => void;
  handleTranslateText: (item: ConvoPost) => void;
  handleShowOriginalText: (item: ConvoPost) => void;
  isHuddleChatTab: boolean;
}

/**
 * ConvoCard Component
 * Renders a conversation message card with support for:
 * - Text messages
 * - Media content (images, videos, audio)
 * - Message translations
 * - User interactions (impressions/reactions)
 * - Reply functionality
 * - Message actions (edit, delete, etc.)
 */
const ConvoCard: FC<ConvoCardProps> = React.memo(
  ({
    data,
    replyValue,
    setreplyValue,
    profile,
    itemHeights,
    setItemHeights,
    convoPost,
    setShowPreview,
    setSingleImage,
    setShowAllImage,
    setHeader,
    HandleEditSet,
    isOpen,
    setIsOpen,
    selectItem,
    setSelectItem,
    showFeedbackImpressions,
    setShowFeedbackImpressions,
    mdlCreateConvoVisible,
    setMdlCreateConvoVisible,
    setShowConvoListModal,
    setMdlConvoVisible,
    setConvoLoading,
    setIsOpenConvo,
    itemEdit,
    setItemEdit,
    handleTranslateText,
    handleShowOriginalText,
    isHuddleChatTab,
  }) => {
    // Get current user from Redux store
    const currentUser = useSelector((state: RootState) => state.user.user[0]);
    const navigate = useNavigate();
    const dimensions = Dimensions.get("window");
    const isMobile = dimensions?.width <= 600 || false;
    const userconvo = useSelector(
      (state: RootState) => state.convo.convo[0],
    ) as ConvoPost;

    // State management
    const [item, setitem] = useState<ConvoPost>(data);
    const menuRef = useRef<HTMLDivElement>(null);
    const [videoStatus, setVideoStatus] = React.useState<string>("");
    const [videoItem, setVideoItem] = React.useState<ConvoPost | null>(null);
    const entitydatalist = useSelector(
      (state: RootState) => state.user.entitydatalist,
    ) as AV.IAVEntity[];
    const [isHovered, setIsHovered] = useState<boolean>(false);

    // Update item when convoPost changes
    useEffect(() => {
      setitem(data);
    }, [convoPost]);

    /**
     * Custom hook to handle clicks outside the menu
     * Closes the menu and feedback impressions when clicking outside
     */
    function useOutsideAlerter(ref: React.RefObject<HTMLDivElement>) {
      React.useEffect(() => {
        function handleClickOutside(event: MouseEvent) {
          if (ref.current && !ref.current.contains(event.target as Node)) {
            setShowFeedbackImpressions(false);
            setIsOpen(false);
          }
        }
        document.addEventListener("mousedown", handleClickOutside);
        return () => {
          document.removeEventListener("mousedown", handleClickOutside);
        };
      }, [ref]);
    }

    const openPreview = (item: ConvoPost): void => {
      setShowPreview(true);
      setSingleImage(item);
      setShowAllImage(item);
      setHeader(true);
    };

    const handleItemLayout = (item: ConvoPost, height: number): void => {
      const index = convoPost?.findIndex((x) => x?.id === item?.id);
      if (index !== -1) {
        const newHeights = [...itemHeights];
        newHeights[index] = height;
        setItemHeights(newHeights);
      }
    };

    // Process message text and translation logic
    let text: string = "";
    let isOwnChat: boolean = false;
    let isTranslated: boolean = false;
    let shouldTranslate: boolean = false;

    const isText = (mtext: string): boolean => {
      const textRegex = /\p{L}/u;
      return textRegex.test(mtext);
    };
    const stripHtml = (html: string): string =>
      html.replace(/<[^>]*>/g, "").trim();
    const hasRealText = (text: string): boolean => isText(stripHtml(text));

    const getBaseLang = (lang: string): string => lang?.split("-")[0] || "";
    const activeItem =
      itemEdit?.id && itemEdit?.id === item?.id ? itemEdit : item;

    // Process text and translation logic
    if (activeItem) {
      text = typeof activeItem.text === "string" ? activeItem.text.trim() : "";
      isOwnChat = currentUser?.id === activeItem?.person?.id;
      isTranslated = activeItem?.isTranslateText ?? false;
      const userLang = currentUser?.preferences?.lang;
      const itemLang = activeItem?.plang;
      shouldTranslate =
        ((text !== "" && hasRealText(text) && !activeItem?.plang) ||
          getBaseLang(userLang || "") !== getBaseLang(itemLang || "")) &&
        text !== "" &&
        hasRealText(text) &&
        item?.ptype !== "W" &&
        item?.ptype !== "G";

      if (
        currentUser?.preferences?.autoTrans &&
        !isOwnChat &&
        activeItem?.isTranslateText === undefined &&
        shouldTranslate &&
        activeItem?.trans &&
        activeItem.trans.length > 0 &&
        activeItem.trans.some(
          (x) =>
            getBaseLang(x?.lang ?? "") ===
            getBaseLang(currentUser?.preferences?.lang ?? ""),
        )
      ) {
        isTranslated = true;
        activeItem.isTranslateText = true;
      }

      if (!isOwnChat && isTranslated && Array.isArray(activeItem.trans)) {
        const userLang = getBaseLang(currentUser?.preferences?.lang ?? "");
        const matchedTranslation = activeItem.trans.find(
          (x) => getBaseLang(x?.lang ?? "") === userLang,
        );
        text = matchedTranslation?.text || text;
      }
    }

    const handleTranslateClick = (item: ConvoPost): void => {
      if (!isTranslated) {
        handleTranslateText(item);
      } else {
        handleShowOriginalText(item);
      }
      setreplyValue();
      setIsOpen(false);
    };

    const handleMoreClick = (item: ConvoPost): void => {
      setIsOpen(!isOpen);
      setSelectItem(item);
    };

    const handleProfileClick = (item: ConvoPost): void => {
      setShowConvoListModal(false);
      setMdlConvoVisible(false);
      setMdlCreateConvoVisible(false);
      setConvoLoading(false);
      setIsOpenConvo(false);
      navigate(`/profile/${item?.person?.id}/${item?.person?.region}`, {
        state: { user: item?.person },
      });
    };

    interface ImpressionCount {
      count: number;
      icon: string;
      alt: string;
    }

    const getImpressionCounts = (
      item: ConvoPost,
    ): Record<string, ImpressionCount> => {
      const impressions: Record<string, ImpressionCount> = {};
      const impressionTypes = [
        "Wow",
        "Lol",
        "Like",
        "Unlike",
        "Cheer",
        "Question",
        "Sad",
        "Mad",
        "heart",
        "broken",
      ];

      impressionTypes.forEach((type) => {
        const count =
          item?.impressions?.filter((x) => x?.impression === type).length || 0;
        if (count > 0) {
          impressions[type] = {
            count,
            icon: settings[type.toLowerCase()],
            alt: type,
          };
        }
      });

      return impressions;
    };

    // Render the conversation card
    return (
      <>
        {/* Render sent message (current user's message) */}
        {item?.person?.id === currentUser?.id ? (
          <View
            zIndex={-3}
            onLayout={({ nativeEvent }) =>
              handleItemLayout(item, nativeEvent.layout.height)
            }
          >
            <VStack
              borderRadius={[5, 10]}
              py={[1, 1]}
              alignItems={isMobile ? "" : "flex-end"}
              zIndex={-3}
            >
              {/* Text Only */}
              {(item?.media?.id?.trim()?.length === undefined ||
                item?.media?.id?.trim()?.length === 0) &&
                (item?.galleryPost?.length === undefined ||
                  item?.galleryPost?.length === 0) && (
                  <>
                    {/* Reply */}
                    {item?.ReplyPost &&
                      item.ReplyPost.length > 0 &&
                      item.ReplyPost[0]?.person?.id && (
                        <>
                          <VStack
                            w={
                              item?.ReplyPost?.[0]?.text?.trim?.() !== ""
                                ? "fit-content"
                                : "unset"
                            }
                            maxW={
                              item?.ReplyPost?.[0]?.text?.trim?.() === ""
                                ? isMobile
                                  ? "180px"
                                  : "260px"
                                : "unset"
                            }
                            padding={"4px"}
                            borderBottomRightRadius={0}
                            borderBottomLeftRadius={0}
                            borderRadius={"10px"}
                            background={"gray.100"}
                            minW={
                              item?.ReplyPost?.[0]?.text?.trim?.() === ""
                                ? isMobile
                                  ? ""
                                  : "100px"
                                : "unset"
                            }
                            marginRight={"15px"}
                            marginTop={"13px"}
                            left={isMobile ? "175px" : ""}
                          >
                            {/* Title */}
                            <HStack>
                              <Text fontWeight={400}>
                                {" "}
                                {item?.ReplyPost[0]?.person?.title}{" "}
                              </Text>
                            </HStack>

                            {/* Text */}
                            {item?.ReplyPost[0].ptype === "H" ? (
                              (() => {
                                let rpyText = item?.ReplyPost[0]
                                  ?.text as string;

                                if (rpyText.includes("background-image")) {
                                  rpyText = rpyText.replace(
                                    /<div([^>]*)style="([^"]*)"/,
                                    (match, p1, p2) => {
                                      if (
                                        /background\s*:\s*(none|unset)/i.test(
                                          p2,
                                        ) ||
                                        /background-image\s*:\s*(none|unset)/i.test(
                                          p2,
                                        )
                                      ) {
                                        return match;
                                      }

                                      const hasHeight = /height\s*:/.test(p2);
                                      const hasWidth = /width\s*:/.test(p2);

                                      let newStyle = p2;
                                      if (!hasHeight)
                                        newStyle += "; height: 480px";
                                      if (!hasWidth)
                                        newStyle += "; width: 480px";

                                      return `<div${p1} style="${newStyle}"`;
                                    },
                                  );
                                }

                                let divWidth = "100%";
                                let divHeight = "auto";
                                rpyText = rpyText.replace(
                                  /<figure[^>]*>(.*?)<\/figure>/g,
                                  "$1",
                                );
                                const htmlPostbgImg = rpyText
                                  ? /background-image\s*:\s*(?!\s*(none|unset)\b)[^;]+/i.test(
                                      rpyText,
                                    )
                                  : false;
                                const divStyleMatch = rpyText.match(
                                  /<div[^>]*style="[^"]*width\s*:\s*(\d+)(?:px)?;[^"]*height\s*:\s*(\d+)(?:px)?;?[^"]*"/i,
                                );

                                if (divStyleMatch) {
                                  divWidth = `${divStyleMatch[1]}px`;
                                  divHeight = `${divStyleMatch[2]}px`;
                                }

                                return (
                                  <div
                                    className="htmlPost"
                                    title="post"
                                    style={{
                                      width: htmlPostbgImg ? divWidth : "100%",
                                      minWidth: htmlPostbgImg
                                        ? divWidth
                                        : "100%",
                                      outline: "none",
                                      userSelect: "text",
                                      cursor: "default",
                                    }}
                                    contentEditable={htmlPostbgImg}
                                    suppressContentEditableWarning={true}
                                    onInput={(e) => e.preventDefault()}
                                    onKeyDown={(e) => e.preventDefault()}
                                    onPaste={(e) => e.preventDefault()}
                                    onFocus={(e) =>
                                      (e.target as HTMLElement).blur()
                                    }
                                    dangerouslySetInnerHTML={{
                                      __html: rpyText,
                                    }}
                                  />
                                );
                              })()
                            ) : (
                              <Text
                                maxW={
                                  item?.ReplyPost?.[0]?.text?.trim?.() === ""
                                    ? "195px"
                                    : "unset"
                                }
                                numberOfLines={
                                  item?.ReplyPost?.[0]?.text?.trim?.() !== ""
                                    ? 0
                                    : undefined
                                }
                                ellipsizeMode={
                                  item?.ReplyPost?.[0]?.text?.trim?.() !== ""
                                    ? "clip"
                                    : undefined
                                }
                              >
                                <AVlinktext
                                  text={item?.ReplyPost[0]?.text as string}
                                />
                              </Text>
                            )}

                            {/* Media */}
                            {item?.ReplyPost[0]?.media?.type?.startsWith(
                              "audio",
                            ) ? (
                              <VoiceMessage
                                audioUrl={Media.getURL(
                                  item?.ReplyPost[0]?.media,
                                )}
                                onRecordingComplete={() => {}}
                              />
                            ) : (
                              //<Pressable
                              //  onPress={() =>
                              //    item?.ReplyPost &&
                              //    item.ReplyPost[0] &&
                              //    openPreview(item.ReplyPost[0])
                              //  }
                              //>
                              <GridView
                                item={item?.ReplyPost[0]}
                                setVideoStatus={setVideoStatus}
                                setVideoItem={setVideoItem}
                                isConvoReply={true}
                              />
                              //</Pressable>
                            )}
                          </VStack>
                        </>
                      )}

                    <VStack>
                      <VStack
                        w={text?.trim?.() !== "" ? "fit-content" : "unset"}
                        maxW={
                          text?.trim?.() === ""
                            ? isMobile
                              ? "180px"
                              : "210px"
                            : item?.ptype === "H"
                              ? "400px"
                              : "unset"
                        }
                        padding={"10px"}
                        borderBottomRightRadius={"10px"}
                        borderBottomLeftRadius={"10px"}
                        background={"#00A7BC"}
                        borderTopRightRadius={"10px"}
                        borderTopLeftRadius={"10px"}
                        minW={
                          text?.trim?.() === ""
                            ? isMobile
                              ? ""
                              : "100px"
                            : item?.ptype === "H"
                              ? "100px"
                              : "unset"
                        }
                        marginBottom={"14px"}
                        marginRight={"15px"}
                        left={isMobile ? "175px" : ""}
                      >
                        {/* Text */}
                        {item?.ptype === "H" ? (
                          (() => {
                            // Inject default width/height if missing
                            if (text.includes("background-image")) {
                              text = text.replace(
                                /<div([^>]*)style="([^"]*)"/,
                                (match, p1, p2) => {
                                  // Skip if background is none or unset
                                  if (
                                    /background\s*:\s*(none|unset)/i.test(p2) ||
                                    /background-image\s*:\s*(none|unset)/i.test(
                                      p2,
                                    )
                                  ) {
                                    return match;
                                  }

                                  const hasHeight = /height\s*:/.test(p2);
                                  const hasWidth = /width\s*:/.test(p2);

                                  let newStyle = p2;
                                  if (!hasHeight) newStyle += "; height: 480px";
                                  if (!hasWidth) newStyle += "; width: 480px";

                                  return `<div${p1} style="${newStyle}"`;
                                },
                              );
                            }

                            let divWidth = "100%";
                            let divHeight = "auto";
                            text = text.replace(
                              /<figure[^>]*>(.*?)<\/figure>/g,
                              "$1",
                            );
                            const htmlPostbgImg = text
                              ? /background-image\s*:\s*(?!\s*(none|unset)\b)[^;]+/i.test(
                                  text,
                                )
                              : false;

                            // Extract width & height from inline style
                            const divStyleMatch = text.match(
                              /<div[^>]*style="[^"]*width\s*:\s*(\d+)(?:px)?;[^"]*height\s*:\s*(\d+)(?:px)?;?[^"]*"/i,
                            );

                            if (divStyleMatch) {
                              divWidth = `${divStyleMatch[1]}px`;
                              divHeight = `${divStyleMatch[2]}px`;
                            }

                            return (
                              <div
                                className="htmlPost"
                                title="post"
                                style={{
                                  width: htmlPostbgImg ? divWidth : "100%",
                                  minWidth: htmlPostbgImg ? divWidth : "100%",
                                  outline: "none", // remove focus outline
                                  userSelect: "text", // allow text selection
                                  cursor: "default", // normal cursor
                                }}
                                contentEditable={htmlPostbgImg} // allows selection but prevent edits
                                suppressContentEditableWarning={true}
                                onInput={(e) => e.preventDefault()} // prevent typing
                                onKeyDown={(e) => e.preventDefault()} // prevent key presses
                                onPaste={(e) => e.preventDefault()} // prevent pasting
                                onFocus={(e) =>
                                  (e.target as HTMLElement).blur()
                                } // prevent caret focus
                                dangerouslySetInnerHTML={{ __html: text }}
                              />
                            );
                          })()
                        ) : (
                          <Text
                            maxW={text?.trim?.() === "" ? "195px" : "unset"}
                            color="#FFFFFF"
                            numberOfLines={
                              text?.trim?.() !== "" ? 0 : undefined
                            } // allows wrapping
                            ellipsizeMode={
                              text?.trim?.() !== "" ? "clip" : undefined
                            } // don’t cut off text
                          >
                            <AVlinktext text={text} />
                          </Text>
                        )}

                        {/* Time */}
                        <HStack bottom={"-5px"}>
                          <Text color={"gray.700"} fontSize={"12px"}>
                            {/*<AVConverttime*/}
                            {/*  date={*/}
                            {/*    item?.createDate instanceof Date*/}
                            {/*      ? item.createDate.toISOString()*/}
                            {/*      : item?.createDate ?? new Date().toISOString()*/}
                            {/*  }*/}
                            {/*  page={"Post"}*/}
                            {/*/>*/}
                            {item?.createDate
                              ? Moment(item.createDate).format("hh:mm A")
                              : ""}
                          </Text>
                        </HStack>

                        {/* My Message Impression of Post */}
                        <HStack position={"absolute"} bottom={"-14px"} left={0}>
                          {item?.impressions &&
                            item.impressions.filter(
                              (x) => x?.impression == "Wow",
                            ).length > 0 && (
                              <Box
                                height={"20px"}
                                width={"30px"}
                                alignItems={"center"}
                                backgroundColor={"white"}
                                rounded={"full"}
                                shadow={9}
                              >
                                <HStack>
                                  <Image
                                    source={{ uri: settings.wow }}
                                    alt="Wow"
                                    size="18"
                                  />
                                  <Text fontSize={11} fontWeight={"bold"}>
                                    {
                                      item.impressions.filter(
                                        (x) => x?.impression == "Wow",
                                      ).length
                                    }
                                  </Text>
                                </HStack>
                              </Box>
                            )}

                          {item?.impressions &&
                            item?.impressions?.filter(
                              (x) => x?.impression == "Lol",
                            ).length > 0 && (
                              <Box
                                height={"20px"}
                                width={"30px"}
                                alignItems={"center"}
                                backgroundColor={"white"}
                                rounded={"full"}
                                shadow={9}
                              >
                                <HStack>
                                  <Image
                                    source={{ uri: settings.lol }}
                                    alt="Lol"
                                    size="18"
                                  />
                                  <Text fontSize={11} fontWeight={"bold"}>
                                    {
                                      item?.impressions?.filter(
                                        (x) => x?.impression == "Lol",
                                      ).length
                                    }
                                  </Text>
                                </HStack>
                              </Box>
                            )}

                          {item?.impressions &&
                            item?.impressions?.filter(
                              (x) => x?.impression == "Like",
                            ).length > 0 && (
                              <Box
                                height={"20px"}
                                width={"30px"}
                                alignItems={"center"}
                                backgroundColor={"white"}
                                rounded={"full"}
                                shadow={9}
                              >
                                <HStack>
                                  <Image
                                    source={{ uri: settings.like }}
                                    alt="Like"
                                    size="18"
                                  />
                                  <Text fontWeight={"bold"} fontSize={"11"}>
                                    {
                                      item?.impressions?.filter(
                                        (x) => x?.impression == "Like",
                                      ).length
                                    }
                                  </Text>
                                </HStack>
                              </Box>
                            )}

                          {item?.impressions &&
                            item.impressions.filter(
                              (x) => x?.impression == "Unlike",
                            ).length > 0 && (
                              <Box
                                height={"20px"}
                                width={"30px"}
                                alignItems={"center"}
                                backgroundColor={"white"}
                                rounded={"full"}
                                shadow={9}
                              >
                                <HStack>
                                  <Image
                                    source={{ uri: settings.unlike }}
                                    alt="Unlike"
                                    size="18"
                                  />
                                  <Text fontWeight={"bold"} fontSize={"11"}>
                                    {
                                      item.impressions.filter(
                                        (x) => x?.impression == "Unlike",
                                      ).length
                                    }
                                  </Text>
                                </HStack>
                              </Box>
                            )}

                          {item?.impressions &&
                            item?.impressions?.filter(
                              (x) => x?.impression == "Cheer",
                            ).length > 0 && (
                              <Box
                                height={"20px"}
                                width={"30px"}
                                alignItems={"center"}
                                backgroundColor={"white"}
                                rounded={"full"}
                                shadow={9}
                              >
                                <HStack>
                                  <Image
                                    source={{ uri: settings.cheer }}
                                    alt="cheer"
                                    size="18"
                                  />
                                  <Text fontWeight={"bold"} fontSize={"11"}>
                                    {
                                      item?.impressions?.filter(
                                        (x) => x?.impression == "Cheer",
                                      ).length
                                    }
                                  </Text>
                                </HStack>
                              </Box>
                            )}

                          {item?.impressions &&
                            item?.impressions?.filter(
                              (x) => x?.impression == "Question",
                            ).length > 0 && (
                              <Box
                                height={"20px"}
                                width={"30px"}
                                alignItems={"center"}
                                backgroundColor={"white"}
                                rounded={"full"}
                                shadow={9}
                              >
                                <HStack>
                                  <Image
                                    source={{ uri: settings.question }}
                                    alt="stars"
                                    size="18"
                                  />
                                  <Text fontWeight={"bold"} fontSize={"11"}>
                                    {
                                      item?.impressions?.filter(
                                        (x) => x?.impression == "Question",
                                      ).length
                                    }
                                  </Text>
                                </HStack>
                              </Box>
                            )}

                          {item?.impressions &&
                            item?.impressions?.filter(
                              (x) => x?.impression == "Sad",
                            ).length > 0 && (
                              <Box
                                height={"20px"}
                                width={"30px"}
                                alignItems={"center"}
                                backgroundColor={"white"}
                                rounded={"full"}
                                shadow={9}
                              >
                                <HStack>
                                  <Image
                                    source={{ uri: settings.sad }}
                                    alt="sad"
                                    size="18"
                                  />
                                  <Text fontWeight={"bold"} fontSize={"11"}>
                                    {
                                      item?.impressions?.filter(
                                        (x) => x?.impression == "Sad",
                                      ).length
                                    }
                                  </Text>
                                </HStack>
                              </Box>
                            )}

                          {item?.impressions &&
                            item?.impressions?.filter(
                              (x) => x?.impression == "Mad",
                            ).length > 0 && (
                              <Box
                                height={"20px"}
                                width={"30px"}
                                alignItems={"center"}
                                backgroundColor={"white"}
                                rounded={"full"}
                                shadow={9}
                              >
                                <HStack>
                                  <Image
                                    source={{ uri: settings.mad }}
                                    alt="Mad"
                                    size="18"
                                  />
                                  <Text fontWeight={"bold"} fontSize={"11"}>
                                    {
                                      item?.impressions?.filter(
                                        (x) => x?.impression == "Mad",
                                      ).length
                                    }
                                  </Text>
                                </HStack>
                              </Box>
                            )}

                          {item?.impressions &&
                            item?.impressions?.filter(
                              (x) => x?.impression == "heart",
                            ).length > 0 && (
                              <Box
                                height={"20px"}
                                width={"30px"}
                                alignItems={"center"}
                                backgroundColor={"white"}
                                rounded={"full"}
                                shadow={9}
                              >
                                <HStack>
                                  <Image
                                    source={{ uri: settings.heart }}
                                    alt="heart"
                                    size="18"
                                  />
                                  <Text fontWeight={"bold"} fontSize={"11"}>
                                    {
                                      item?.impressions?.filter(
                                        (x) => x?.impression == "heart",
                                      ).length
                                    }
                                  </Text>
                                </HStack>
                              </Box>
                            )}

                          {item?.impressions &&
                            item?.impressions?.filter(
                              (x) => x?.impression == "broken",
                            ).length > 0 && (
                              <Box
                                height={"20px"}
                                width={"30px"}
                                alignItems={"center"}
                                backgroundColor={"white"}
                                rounded={"full"}
                                shadow={9}
                              >
                                <HStack>
                                  <Image
                                    source={{ uri: settings.broken }}
                                    alt="sad"
                                    size="18"
                                  />
                                  <Text fontWeight={"bold"} fontSize={"11"}>
                                    {
                                      item?.impressions?.filter(
                                        (x) => x?.impression == "broken",
                                      ).length
                                    }
                                  </Text>
                                </HStack>
                              </Box>
                            )}
                        </HStack>
                      </VStack>

                      {/* More Options */}
                      {item?.id && (
                        <Pressable
                          position={"absolute"}
                          right={"-5px"}
                          top={"10px"}
                          onPress={() => {
                            setIsOpen(!isOpen);
                            setSelectItem(item);
                          }}
                        >
                          <MoreHorizontalIcon
                            height={"4px"}
                            width={"16px"}
                            size={"md"}
                            color={"gray.400"}
                            style={{ transform: [{ rotate: "90deg" }] }}
                          />
                        </Pressable>
                      )}
                    </VStack>
                  </>
                )}

              {/* Media with Text */}
              {((item?.media && item?.media?.id?.trim()?.length > 0) ||
                (item?.galleryPost && item?.galleryPost?.length > 0)) && (
                <>
                  {/* Reply */}
                  {item?.ReplyPost &&
                    item.ReplyPost.length > 0 &&
                    item.ReplyPost[0]?.person?.id && (
                      <>
                        <VStack
                          w={
                            item?.ReplyPost?.[0]?.text?.trim?.() !== ""
                              ? "fit-content"
                              : "260px"
                          }
                          //maxW={"260px"}
                          padding={"4px"}
                          borderBottomRightRadius={0}
                          borderBottomLeftRadius={0}
                          borderRadius={"10px"}
                          background={"gray.100"}
                          minW={"100px"}
                          marginRight={"15px"}
                          marginTop={"13px"}
                        >
                          <HStack>
                            <Text fontWeight={400}>
                              {" "}
                              {item?.ReplyPost[0]?.person?.title}{" "}
                            </Text>
                          </HStack>

                          {item?.ReplyPost[0].ptype === "H" ? (
                            (() => {
                              let rpyText = item?.ReplyPost[0]?.text as string;

                              if (rpyText.includes("background-image")) {
                                rpyText = rpyText.replace(
                                  /<div([^>]*)style="([^"]*)"/,
                                  (match, p1, p2) => {
                                    if (
                                      /background\s*:\s*(none|unset)/i.test(
                                        p2,
                                      ) ||
                                      /background-image\s*:\s*(none|unset)/i.test(
                                        p2,
                                      )
                                    ) {
                                      return match;
                                    }

                                    const hasHeight = /height\s*:/.test(p2);
                                    const hasWidth = /width\s*:/.test(p2);

                                    let newStyle = p2;
                                    if (!hasHeight)
                                      newStyle += "; height: 480px";
                                    if (!hasWidth) newStyle += "; width: 480px";

                                    return `<div${p1} style="${newStyle}"`;
                                  },
                                );
                              }

                              let divWidth = "100%";
                              let divHeight = "auto";
                              rpyText = rpyText.replace(
                                /<figure[^>]*>(.*?)<\/figure>/g,
                                "$1",
                              );
                              const htmlPostbgImg = rpyText
                                ? /background-image\s*:\s*(?!\s*(none|unset)\b)[^;]+/i.test(
                                    rpyText,
                                  )
                                : false;
                              const divStyleMatch = rpyText.match(
                                /<div[^>]*style="[^"]*width\s*:\s*(\d+)(?:px)?;[^"]*height\s*:\s*(\d+)(?:px)?;?[^"]*"/i,
                              );

                              if (divStyleMatch) {
                                divWidth = `${divStyleMatch[1]}px`;
                                divHeight = `${divStyleMatch[2]}px`;
                              }

                              return (
                                <div
                                  className="htmlPost"
                                  title="post"
                                  style={{
                                    width: htmlPostbgImg ? divWidth : "100%",
                                    minWidth: htmlPostbgImg ? divWidth : "100%",
                                    outline: "none",
                                    userSelect: "text",
                                    cursor: "default",
                                  }}
                                  contentEditable={htmlPostbgImg}
                                  suppressContentEditableWarning={true}
                                  onInput={(e) => e.preventDefault()}
                                  onKeyDown={(e) => e.preventDefault()}
                                  onPaste={(e) => e.preventDefault()}
                                  onFocus={(e) =>
                                    (e.target as HTMLElement).blur()
                                  }
                                  dangerouslySetInnerHTML={{ __html: rpyText }}
                                />
                              );
                            })()
                          ) : (
                            <Text
                              maxW={
                                item?.ReplyPost?.[0]?.text?.trim?.() === ""
                                  ? "195px"
                                  : "unset"
                              }
                              numberOfLines={
                                item?.ReplyPost?.[0]?.text?.trim?.() !== ""
                                  ? 0
                                  : undefined
                              }
                              ellipsizeMode={
                                item?.ReplyPost?.[0]?.text?.trim?.() !== ""
                                  ? "clip"
                                  : undefined
                              }
                            >
                              <AVlinktext
                                text={item?.ReplyPost[0]?.text as string}
                              />
                            </Text>
                          )}

                          {item?.ReplyPost[0]?.media?.type?.startsWith(
                            "audio",
                          ) ? (
                            <VoiceMessage
                              audioUrl={Media.getURL(item?.ReplyPost[0]?.media)}
                              onRecordingComplete={() => {}}
                            />
                          ) : (
                            <Pressable
                              onPress={() =>
                                item?.ReplyPost &&
                                item.ReplyPost[0] &&
                                openPreview(item.ReplyPost[0])
                              }
                            >
                              <GridView
                                item={item?.ReplyPost[0]}
                                setVideoStatus={setVideoStatus}
                                setVideoItem={setVideoItem}
                              />
                            </Pressable>
                          )}
                        </VStack>
                      </>
                    )}

                  <VStack
                    w={
                      text?.trim?.() !== ""
                        ? "fit-content"
                        : isHuddleChatTab
                          ? "360px"
                          : "260px"
                    }
                    //width={"260px"}
                    alignSelf={"flex-end"}
                    borderBottomRightRadius={"10px"}
                    borderBottomLeftRadius={"10px"}
                    background={"#00A7BC"}
                    borderTopRightRadius={"10px"}
                    borderTopLeftRadius={"10px"}
                    marginRight={"15px"}
                    padding={"4px"}
                    marginBottom={"14px"}
                  >
                    {/* Text */}
                    {item?.ptype === "H"
                      ? (() => {
                          if (text.includes("background-image")) {
                            text = text.replace(
                              /<div([^>]*)style="([^"]*)"/,
                              (match, p1, p2) => {
                                if (
                                  /background\s*:\s*(none|unset)/i.test(p2) ||
                                  /background-image\s*:\s*(none|unset)/i.test(
                                    p2,
                                  )
                                ) {
                                  return match;
                                }

                                const hasHeight = /height\s*:/.test(p2);
                                const hasWidth = /width\s*:/.test(p2);

                                let newStyle = p2;
                                if (!hasHeight) newStyle += "; height: 480px";
                                if (!hasWidth) newStyle += "; width: 480px";

                                return `<div${p1} style="${newStyle}"`;
                              },
                            );
                          }

                          let divWidth = "100%";
                          let divHeight = "auto";
                          text = text.replace(
                            /<figure[^>]*>(.*?)<\/figure>/g,
                            "$1",
                          );
                          const htmlPostbgImg = text
                            ? /background-image\s*:\s*(?!\s*(none|unset)\b)[^;]+/i.test(
                                text,
                              )
                            : false;
                          const divStyleMatch = text.match(
                            /<div[^>]*style="[^"]*width\s*:\s*(\d+)(?:px)?;[^"]*height\s*:\s*(\d+)(?:px)?;?[^"]*"/i,
                          );

                          if (divStyleMatch) {
                            divWidth = `${divStyleMatch[1]}px`;
                            divHeight = `${divStyleMatch[2]}px`;
                          }

                          return (
                            <div
                              className="htmlPost"
                              title="post"
                              style={{
                                width: htmlPostbgImg ? divWidth : "100%",
                                minWidth: htmlPostbgImg ? divWidth : "100%",
                                outline: "none",
                                userSelect: "text",
                                cursor: "default",
                              }}
                              contentEditable={htmlPostbgImg}
                              suppressContentEditableWarning={true}
                              onInput={(e) => e.preventDefault()}
                              onKeyDown={(e) => e.preventDefault()}
                              onPaste={(e) => e.preventDefault()}
                              onFocus={(e) => (e.target as HTMLElement).blur()}
                              dangerouslySetInnerHTML={{ __html: text }}
                            />
                          );
                        })()
                      : item?.text && (
                          <HStack paddingBottom={"8px"}>
                            <Text
                              width={text?.trim?.() === "" ? "100%" : "unset"}
                              color="#FFFFFF"
                              numberOfLines={
                                text?.trim?.() !== "" ? 0 : undefined
                              }
                              ellipsizeMode={
                                text?.trim?.() !== "" ? "clip" : undefined
                              }
                            >
                              <AVlinktext text={text} />
                            </Text>
                          </HStack>
                        )}

                    {/* Media */}
                    {item?.media?.type?.startsWith("audio") ? (
                      <VoiceMessage
                        audioUrl={Media.getURL(item?.media)}
                        onRecordingComplete={() => {}}
                      />
                    ) : (
                      <Pressable onPress={() => openPreview(item)}>
                        <GridView
                          item={item}
                          setVideoStatus={setVideoStatus}
                          setVideoItem={setVideoItem}
                        />
                      </Pressable>
                    )}

                    {/* Time */}
                    <HStack bottom={"-4px"}>
                      <Text color={"gray.700"} fontSize={"12px"}>
                        {item?.createDate
                          ? Moment(item.createDate).format("hh:mm A")
                          : ""}
                      </Text>
                    </HStack>

                    {/* My message Impression of post */}
                    <HStack position={"absolute"} bottom={"-14px"} left={0}>
                      {item?.impressions &&
                        item.impressions.filter((x) => x?.impression == "Wow")
                          .length > 0 && (
                          <Box
                            height={"20px"}
                            width={"30px"}
                            alignItems={"center"}
                            backgroundColor={"white"}
                            rounded={"full"}
                            shadow={9}
                          >
                            <HStack>
                              <Image
                                source={{ uri: settings.wow }}
                                alt="Wow"
                                size="18"
                              />
                              <Text fontSize={11} fontWeight={"bold"}>
                                {
                                  item.impressions.filter(
                                    (x) => x?.impression == "Wow",
                                  ).length
                                }
                              </Text>
                            </HStack>
                          </Box>
                        )}

                      {item?.impressions &&
                        item?.impressions?.filter((x) => x?.impression == "Lol")
                          .length > 0 && (
                          <Box
                            height={"20px"}
                            width={"30px"}
                            alignItems={"center"}
                            backgroundColor={"white"}
                            rounded={"full"}
                            shadow={9}
                          >
                            <HStack>
                              <Image
                                source={{ uri: settings.lol }}
                                alt="Lol"
                                size="18"
                              />
                              <Text fontSize={11} fontWeight={"bold"}>
                                {
                                  item?.impressions?.filter(
                                    (x) => x?.impression == "Lol",
                                  ).length
                                }
                              </Text>
                            </HStack>
                          </Box>
                        )}

                      {item?.impressions &&
                        item?.impressions?.filter(
                          (x) => x?.impression == "Like",
                        ).length > 0 && (
                          <Box
                            height={"20px"}
                            width={"30px"}
                            alignItems={"center"}
                            backgroundColor={"white"}
                            rounded={"full"}
                            shadow={9}
                          >
                            <HStack>
                              <Image
                                source={{ uri: settings.like }}
                                alt="Like"
                                size="18"
                              />
                              <Text fontWeight={"bold"} fontSize={"11"}>
                                {
                                  item?.impressions?.filter(
                                    (x) => x?.impression == "Like",
                                  ).length
                                }
                              </Text>
                            </HStack>
                          </Box>
                        )}

                      {item?.impressions &&
                        item.impressions.filter(
                          (x) => x?.impression == "Unlike",
                        ).length > 0 && (
                          <Box
                            height={"20px"}
                            width={"30px"}
                            alignItems={"center"}
                            backgroundColor={"white"}
                            rounded={"full"}
                            shadow={9}
                          >
                            <HStack>
                              <Image
                                source={{ uri: settings.unlike }}
                                alt="Unlike"
                                size="18"
                              />
                              <Text fontWeight={"bold"} fontSize={"11"}>
                                {
                                  item.impressions.filter(
                                    (x) => x?.impression == "Unlike",
                                  ).length
                                }
                              </Text>
                            </HStack>
                          </Box>
                        )}

                      {item?.impressions &&
                        item?.impressions?.filter(
                          (x) => x?.impression == "Cheer",
                        ).length > 0 && (
                          <Box
                            height={"20px"}
                            width={"30px"}
                            alignItems={"center"}
                            backgroundColor={"white"}
                            rounded={"full"}
                            shadow={9}
                          >
                            <HStack>
                              <Image
                                source={{ uri: settings.cheer }}
                                alt="cheer"
                                size="18"
                              />
                              <Text fontWeight={"bold"} fontSize={"11"}>
                                {
                                  item?.impressions?.filter(
                                    (x) => x?.impression == "Cheer",
                                  ).length
                                }
                              </Text>
                            </HStack>
                          </Box>
                        )}

                      {item?.impressions &&
                        item?.impressions?.filter(
                          (x) => x?.impression == "Question",
                        ).length > 0 && (
                          <Box
                            height={"20px"}
                            width={"30px"}
                            alignItems={"center"}
                            backgroundColor={"white"}
                            rounded={"full"}
                            shadow={9}
                          >
                            <HStack>
                              <Image
                                source={{ uri: settings.question }}
                                alt="Question"
                                size="18"
                              />
                              <Text fontWeight={"bold"} fontSize={"11"}>
                                {
                                  item?.impressions?.filter(
                                    (x) => x?.impression == "Question",
                                  ).length
                                }
                              </Text>
                            </HStack>
                          </Box>
                        )}

                      {item?.impressions &&
                        item?.impressions?.filter((x) => x?.impression == "Sad")
                          .length > 0 && (
                          <Box
                            height={"20px"}
                            width={"30px"}
                            alignItems={"center"}
                            backgroundColor={"white"}
                            rounded={"full"}
                            shadow={9}
                          >
                            <HStack>
                              <Image
                                source={{ uri: settings.sad }}
                                alt="sads"
                                size="18"
                              />
                              <Text fontWeight={"bold"} fontSize={"11"}>
                                {
                                  item?.impressions?.filter(
                                    (x) => x?.impression == "Sad",
                                  ).length
                                }
                              </Text>
                            </HStack>
                          </Box>
                        )}

                      {item?.impressions &&
                        item?.impressions?.filter((x) => x?.impression == "Mad")
                          .length > 0 && (
                          <Box
                            height={"20px"}
                            width={"30px"}
                            alignItems={"center"}
                            backgroundColor={"white"}
                            rounded={"full"}
                            shadow={9}
                          >
                            <HStack>
                              <Image
                                source={{ uri: settings.mad }}
                                alt="sad"
                                size="18"
                              />
                              <Text fontWeight={"bold"} fontSize={"11"}>
                                {
                                  item?.impressions?.filter(
                                    (x) => x?.impression == "Mad",
                                  ).length
                                }
                              </Text>
                            </HStack>
                          </Box>
                        )}

                      {item?.impressions &&
                        item?.impressions?.filter(
                          (x) => x?.impression == "heart",
                        ).length > 0 && (
                          <Box
                            height={"20px"}
                            width={"30px"}
                            alignItems={"center"}
                            backgroundColor={"white"}
                            rounded={"full"}
                            shadow={9}
                          >
                            <HStack>
                              <Image
                                source={{ uri: settings.heart }}
                                alt="heart"
                                size="18"
                              />
                              <Text fontWeight={"bold"} fontSize={"11"}>
                                {
                                  item?.impressions?.filter(
                                    (x) => x?.impression == "heart",
                                  ).length
                                }
                              </Text>
                            </HStack>
                          </Box>
                        )}

                      {item?.impressions &&
                        item?.impressions?.filter(
                          (x) => x?.impression == "broken",
                        ).length > 0 && (
                          <Box
                            height={"20px"}
                            width={"30px"}
                            alignItems={"center"}
                            backgroundColor={"white"}
                            rounded={"full"}
                            shadow={9}
                          >
                            <HStack>
                              <Image
                                source={{ uri: settings.broken }}
                                alt="broken"
                                size="18"
                              />
                              <Text fontWeight={"bold"} fontSize={"11"}>
                                {
                                  item?.impressions?.filter(
                                    (x) => x?.impression == "broken",
                                  ).length
                                }
                              </Text>
                            </HStack>
                          </Box>
                        )}
                    </HStack>
                  </VStack>

                  {/* More Options */}
                  {item?.id && (
                    <Pressable
                      position={"absolute"}
                      right={"-5px"}
                      top={"10px"}
                      onPress={() => {
                        setIsOpen(!isOpen);
                        setSelectItem(item);
                      }}
                    >
                      <MoreHorizontalIcon
                        height={"4px"}
                        width={"16px"}
                        size={"md"}
                        color={"gray.400"}
                        style={{ transform: [{ rotate: "90deg" }] }}
                      />
                    </Pressable>
                  )}
                </>
              )}
            </VStack>
          </View>
        ) : (
          /* Render received message (other user's message) */
          <View
            zIndex={-3}
            onLayout={({ nativeEvent }) =>
              handleItemLayout(item, nativeEvent.layout.height)
            }
          >
            <VStack
              key={item?.id}
              borderRadius={[5, 10]}
              py={[1, 1]}
              zIndex={-3}
            >
              <HStack space={2} zIndex={-3}>
                {/* Profile and Name */}
                <HStack>
                  <Pressable
                    onPress={() => {
                      setShowConvoListModal(false);
                      setMdlConvoVisible(false);
                      setMdlCreateConvoVisible(false);
                      setConvoLoading(false);
                      setIsOpenConvo(false);
                      navigate(
                        `/profile/${item?.person?.id}/${item?.person?.region}`,
                        { state: { user: item?.person } },
                      );
                    }}
                  >
                    <ProfilePic
                      width={38}
                      height={38}
                      link={
                        (currentUser?.id == item?.person?.id
                          ? AV.AVEntity.getLink(currentUser)
                          : entitydatalist?.find(
                                (x: IAVEntity) => x?.id == item?.person?.id,
                              )
                            ? AV.AVEntity.getLink(
                                entitydatalist.find(
                                  (x: IAVEntity) => x?.id == item?.person?.id,
                                ) as IAVEntity,
                              )
                            : profile
                              ? profile
                              : item?.person) as ILink
                      }
                    />
                  </Pressable>
                </HStack>

                {/* Text Only */}
                {(item?.media?.id?.trim()?.length === undefined ||
                  item?.media?.id?.trim()?.length === 0) &&
                  (item?.galleryPost?.length === undefined ||
                    item?.galleryPost?.length === 0) && (
                    <>
                      <VStack
                        maxW={
                          isHuddleChatTab
                            ? "500px"
                            : item?.ptype === "H" &&
                                !isOwnChat &&
                                shouldTranslate
                              ? "300px"
                              : "350px"
                        }
                        w="fit-content"
                        padding={"4px"}
                        borderBottomRightRadius={"10px"}
                        borderBottomLeftRadius={"10px"}
                        background={"#00000007"}
                        borderTopRightRadius={"10px"}
                        borderTopLeftRadius={"10px"}
                        minW={"100px"}
                        marginRight={"15px"}
                        marginTop={"13px"}
                        marginBottom={"18px"}
                      >
                        {/* Reply */}
                        {item?.ReplyPost &&
                          item.ReplyPost.length > 0 &&
                          item.ReplyPost[0]?.person?.id && (
                            <>
                              <VStack
                                w={
                                  item?.ReplyPost?.[0]?.text?.trim?.() !== ""
                                    ? "fit-content"
                                    : "unset"
                                }
                                maxW={
                                  item?.ReplyPost?.[0]?.text?.trim?.() === ""
                                    ? "210px"
                                    : "unset"
                                }
                                padding={"10px"}
                                borderBottomRightRadius={0}
                                borderBottomLeftRadius={0}
                                borderRadius={"10px"}
                                background={"gray.100"}
                                minW={"100px"}
                                marginRight={"15px"}
                                marginTop={"0px"}
                                marginBottom={"5px"}
                                left={
                                  item?.ReplyPost[0]?.media?.type ===
                                  "audio/wav"
                                    ? "-10px"
                                    : "0px"
                                }
                              >
                                {/* Reply person */}
                                <HStack>
                                  <Text fontWeight={400}>
                                    {" "}
                                    {item?.ReplyPost[0]?.person?.title}{" "}
                                  </Text>
                                </HStack>

                                {/* Text */}
                                {item?.ReplyPost[0].ptype === "H" ? (
                                  (() => {
                                    let rpyText = item?.ReplyPost[0]
                                      ?.text as string;

                                    if (rpyText.includes("background-image")) {
                                      rpyText = rpyText.replace(
                                        /<div([^>]*)style="([^"]*)"/,
                                        (match, p1, p2) => {
                                          if (
                                            /background\s*:\s*(none|unset)/i.test(
                                              p2,
                                            ) ||
                                            /background-image\s*:\s*(none|unset)/i.test(
                                              p2,
                                            )
                                          ) {
                                            return match;
                                          }

                                          const hasHeight = /height\s*:/.test(
                                            p2,
                                          );
                                          const hasWidth = /width\s*:/.test(p2);

                                          let newStyle = p2;
                                          if (!hasHeight)
                                            newStyle += "; height: 480px";
                                          if (!hasWidth)
                                            newStyle += "; width: 480px";

                                          return `<div${p1} style="${newStyle}"`;
                                        },
                                      );
                                    }

                                    let divWidth = "100%";
                                    let divHeight = "auto";
                                    rpyText = rpyText.replace(
                                      /<figure[^>]*>(.*?)<\/figure>/g,
                                      "$1",
                                    );
                                    const htmlPostbgImg = rpyText
                                      ? /background-image\s*:\s*(?!\s*(none|unset)\b)[^;]+/i.test(
                                          rpyText,
                                        )
                                      : false;
                                    const divStyleMatch = rpyText.match(
                                      /<div[^>]*style="[^"]*width\s*:\s*(\d+)(?:px)?;[^"]*height\s*:\s*(\d+)(?:px)?;?[^"]*"/i,
                                    );

                                    if (divStyleMatch) {
                                      divWidth = `${divStyleMatch[1]}px`;
                                      divHeight = `${divStyleMatch[2]}px`;
                                    }

                                    return (
                                      <div
                                        className="htmlPost"
                                        title="post"
                                        style={{
                                          width: htmlPostbgImg
                                            ? divWidth
                                            : "100%",
                                          minWidth: htmlPostbgImg
                                            ? divWidth
                                            : "100%",
                                          outline: "none",
                                          userSelect: "text",
                                          cursor: "default",
                                        }}
                                        contentEditable={htmlPostbgImg}
                                        suppressContentEditableWarning={true}
                                        onInput={(e) => e.preventDefault()}
                                        onKeyDown={(e) => e.preventDefault()}
                                        onPaste={(e) => e.preventDefault()}
                                        onFocus={(e) =>
                                          (e.target as HTMLElement).blur()
                                        }
                                        dangerouslySetInnerHTML={{
                                          __html: rpyText,
                                        }}
                                      />
                                    );
                                  })()
                                ) : (
                                  <Text
                                    maxW={
                                      item?.ReplyPost?.[0]?.text?.trim?.() ===
                                      ""
                                        ? "195px"
                                        : "unset"
                                    }
                                    numberOfLines={
                                      item?.ReplyPost?.[0]?.text?.trim?.() !==
                                      ""
                                        ? 0
                                        : undefined
                                    }
                                    ellipsizeMode={
                                      item?.ReplyPost?.[0]?.text?.trim?.() !==
                                      ""
                                        ? "clip"
                                        : undefined
                                    }
                                  >
                                    <AVlinktext
                                      text={item?.ReplyPost[0]?.text as string}
                                    />
                                  </Text>
                                )}

                                {/* Media */}
                                {item?.ReplyPost[0]?.media?.type?.startsWith(
                                  "audio",
                                ) ? (
                                  <VoiceMessage
                                    audioUrl={Media.getURL(
                                      item?.ReplyPost[0]?.media,
                                    )}
                                    onRecordingComplete={() => {}}
                                  />
                                ) : (
                                  //<Pressable
                                  //  onPress={() =>
                                  //    item?.ReplyPost &&
                                  //    item.ReplyPost[0] &&
                                  //    openPreview(item.ReplyPost[0])
                                  //  }
                                  //>
                                  <GridView
                                    item={item?.ReplyPost[0]}
                                    setVideoStatus={setVideoStatus}
                                    setVideoItem={setVideoItem}
                                    isConvoReply={true}
                                  />
                                  //</Pressable>
                                )}
                              </VStack>
                            </>
                          )}

                        {/* Title */}
                        <HStack
                          position={"relative"}
                          space={1}
                          width={"200px"}
                          zIndex={0}
                        >
                          <Text
                            fontSize={"15px"}
                            width={"100%"}
                            isTruncated
                            color={"black"}
                            fontWeight={"600"}
                          >
                            {(profile as ILink)?.title ||
                              (item?.person as AV.ILink)?.title}
                          </Text>
                        </HStack>

                        {/* Text */}
                        {item?.ptype === "H" ? (
                          (() => {
                            if (text.includes("background-image")) {
                              text = text.replace(
                                /<div([^>]*)style="([^"]*)"/,
                                (match, p1, p2) => {
                                  if (
                                    /background\s*:\s*(none|unset)/i.test(p2) ||
                                    /background-image\s*:\s*(none|unset)/i.test(
                                      p2,
                                    )
                                  ) {
                                    return match;
                                  }

                                  const hasHeight = /height\s*:/.test(p2);
                                  const hasWidth = /width\s*:/.test(p2);

                                  let newStyle = p2;
                                  if (!hasHeight) newStyle += "; height: 480px";
                                  if (!hasWidth) newStyle += "; width: 480px";

                                  return `<div${p1} style="${newStyle}"`;
                                },
                              );
                            }

                            let divWidth = "100%";
                            let divHeight = "auto";
                            text = text.replace(
                              /<figure[^>]*>(.*?)<\/figure>/g,
                              "$1",
                            );
                            const htmlPostbgImg = text
                              ? /background-image\s*:\s*(?!\s*(none|unset)\b)[^;]+/i.test(
                                  text,
                                )
                              : false;
                            const divStyleMatch = text.match(
                              /<div[^>]*style="[^"]*width\s*:\s*(\d+)(?:px)?;[^"]*height\s*:\s*(\d+)(?:px)?;?[^"]*"/i,
                            );

                            if (divStyleMatch) {
                              divWidth = `${divStyleMatch[1]}px`;
                              divHeight = `${divStyleMatch[2]}px`;
                            }

                            return (
                              <div
                                className="htmlPost"
                                title="post"
                                style={{
                                  width: htmlPostbgImg ? divWidth : "100%",
                                  minWidth: htmlPostbgImg ? divWidth : "100%",
                                  outline: "none",
                                  userSelect: "text",
                                  cursor: "default",
                                }}
                                contentEditable={htmlPostbgImg}
                                suppressContentEditableWarning={true}
                                onInput={(e) => e.preventDefault()}
                                onKeyDown={(e) => e.preventDefault()}
                                onPaste={(e) => e.preventDefault()}
                                onFocus={(e) =>
                                  (e.target as HTMLElement).blur()
                                }
                                dangerouslySetInnerHTML={{ __html: text }}
                              />
                            );
                          })()
                        ) : (
                          <VStack
                            paddingBottom={"8px"}
                            width={"100%"}
                            paddingTop={"2px"}
                            position={"relative"}
                            zIndex={0}
                          >
                            <Text
                              maxW={text?.trim?.() === "" ? "195px" : "unset"}
                              color={"black"}
                              fontSize={"14px"}
                              fontWeight={"400"}
                              lineHeight={"20px"}
                              numberOfLines={
                                text?.trim?.() !== "" ? 0 : undefined
                              }
                              ellipsizeMode={
                                text?.trim?.() !== "" ? "clip" : undefined
                              }
                            >
                              {text}
                            </Text>
                          </VStack>
                        )}

                        {/* Time */}
                        <HStack bottom={"-5px"}>
                          <Text color={"gray.700"} fontSize={"12px"}>
                            {item?.createDate
                              ? Moment(item.createDate).format("hh:mm A")
                              : ""}
                          </Text>
                        </HStack>

                        {/* Received Message Impression of Post */}
                        <HStack
                          position={"absolute"}
                          bottom={"-12px"}
                          right={0}
                        >
                          {item?.impressions &&
                            item.impressions.filter(
                              (x) => x?.impression == "Wow",
                            ).length > 0 && (
                              <Box
                                height={"20px"}
                                width={"30px"}
                                alignItems={"center"}
                                backgroundColor={"white"}
                                rounded={"full"}
                                shadow={9}
                              >
                                <HStack>
                                  <Image
                                    source={{ uri: settings.wow }}
                                    alt="startruck"
                                    size="18"
                                  />
                                  <Text fontSize={11} fontWeight={"bold"}>
                                    {
                                      item.impressions.filter(
                                        (x) => x?.impression == "Wow",
                                      ).length
                                    }
                                  </Text>
                                </HStack>
                              </Box>
                            )}

                          {item?.impressions &&
                            item?.impressions?.filter(
                              (x) => x?.impression == "Lol",
                            ).length > 0 && (
                              <Box
                                height={"20px"}
                                width={"30px"}
                                alignItems={"center"}
                                backgroundColor={"white"}
                                rounded={"full"}
                                shadow={9}
                              >
                                <HStack>
                                  <Image
                                    source={{ uri: settings.lol }}
                                    alt="Lol"
                                    size="18"
                                  />
                                  <Text fontSize={11} fontWeight={"bold"}>
                                    {
                                      item?.impressions?.filter(
                                        (x) => x?.impression == "Lol",
                                      ).length
                                    }
                                  </Text>
                                </HStack>
                              </Box>
                            )}

                          {item?.impressions &&
                            item?.impressions?.filter(
                              (x) => x?.impression == "Like",
                            ).length > 0 && (
                              <Box
                                height={"20px"}
                                width={"30px"}
                                alignItems={"center"}
                                backgroundColor={"white"}
                                rounded={"full"}
                                shadow={9}
                              >
                                <HStack>
                                  <Image
                                    source={{ uri: settings.like }}
                                    alt="Like"
                                    size="18"
                                  />
                                  <Text fontWeight={"bold"} fontSize={"11"}>
                                    {
                                      item?.impressions?.filter(
                                        (x) => x?.impression == "Like",
                                      ).length
                                    }
                                  </Text>
                                </HStack>
                              </Box>
                            )}

                          {item?.impressions &&
                            item.impressions.filter(
                              (x) => x?.impression == "Unlike",
                            ).length > 0 && (
                              <Box
                                height={"20px"}
                                width={"30px"}
                                alignItems={"center"}
                                backgroundColor={"white"}
                                rounded={"full"}
                                shadow={9}
                              >
                                <HStack>
                                  <Image
                                    source={{ uri: settings.unlike }}
                                    alt="Unlike"
                                    size="18"
                                  />
                                  <Text fontWeight={"bold"} fontSize={"11"}>
                                    {
                                      item.impressions.filter(
                                        (x) => x?.impression == "Unlike",
                                      ).length
                                    }
                                  </Text>
                                </HStack>
                              </Box>
                            )}

                          {item?.impressions &&
                            item?.impressions?.filter(
                              (x) => x?.impression == "Cheer",
                            ).length > 0 && (
                              <Box
                                height={"20px"}
                                width={"30px"}
                                alignItems={"center"}
                                backgroundColor={"white"}
                                rounded={"full"}
                                shadow={9}
                              >
                                <HStack>
                                  <Image
                                    source={{ uri: settings.cheer }}
                                    alt="cheer"
                                    size="18"
                                  />
                                  <Text fontWeight={"bold"} fontSize={"11"}>
                                    {
                                      item?.impressions?.filter(
                                        (x) => x?.impression == "Cheer",
                                      ).length
                                    }
                                  </Text>
                                </HStack>
                              </Box>
                            )}

                          {item?.impressions &&
                            item?.impressions?.filter(
                              (x) => x?.impression == "Question",
                            ).length > 0 && (
                              <Box
                                height={"20px"}
                                width={"30px"}
                                alignItems={"center"}
                                backgroundColor={"white"}
                                rounded={"full"}
                                shadow={9}
                              >
                                <HStack>
                                  <Image
                                    source={{ uri: settings.question }}
                                    alt="stars"
                                    size="18"
                                  />
                                  <Text fontWeight={"bold"} fontSize={"11"}>
                                    {
                                      item?.impressions?.filter(
                                        (x) => x?.impression == "Question",
                                      ).length
                                    }
                                  </Text>
                                </HStack>
                              </Box>
                            )}

                          {item?.impressions &&
                            item?.impressions?.filter(
                              (x) => x?.impression == "Sad",
                            ).length > 0 && (
                              <Box
                                height={"20px"}
                                width={"30px"}
                                alignItems={"center"}
                                backgroundColor={"white"}
                                rounded={"full"}
                                shadow={9}
                              >
                                <HStack>
                                  <Image
                                    source={{ uri: settings.sad }}
                                    alt="sads"
                                    size="18"
                                  />
                                  <Text fontWeight={"bold"} fontSize={"11"}>
                                    {
                                      item?.impressions?.filter(
                                        (x) => x?.impression == "Sad",
                                      ).length
                                    }
                                  </Text>
                                </HStack>
                              </Box>
                            )}

                          {item?.impressions &&
                            item?.impressions?.filter(
                              (x) => x?.impression == "Mad",
                            ).length > 0 && (
                              <Box
                                height={"20px"}
                                width={"30px"}
                                alignItems={"center"}
                                backgroundColor={"white"}
                                rounded={"full"}
                                shadow={9}
                              >
                                <HStack>
                                  <Image
                                    source={{ uri: settings.mad }}
                                    alt="sad"
                                    size="18"
                                  />
                                  <Text fontWeight={"bold"} fontSize={"11"}>
                                    {
                                      item?.impressions?.filter(
                                        (x) => x?.impression == "Mad",
                                      ).length
                                    }
                                  </Text>
                                </HStack>
                              </Box>
                            )}

                          {item?.impressions &&
                            item?.impressions?.filter(
                              (x) => x?.impression == "heart",
                            ).length > 0 && (
                              <Box
                                height={"20px"}
                                width={"30px"}
                                alignItems={"center"}
                                backgroundColor={"white"}
                                rounded={"full"}
                                shadow={9}
                              >
                                <HStack>
                                  <Image
                                    source={{ uri: settings.heart }}
                                    alt="heart"
                                    size="18"
                                  />
                                  <Text fontWeight={"bold"} fontSize={"11"}>
                                    {
                                      item?.impressions?.filter(
                                        (x) => x?.impression == "heart",
                                      ).length
                                    }
                                  </Text>
                                </HStack>
                              </Box>
                            )}

                          {item?.impressions &&
                            item?.impressions?.filter(
                              (x) => x?.impression == "broken",
                            ).length > 0 && (
                              <Box
                                height={"20px"}
                                width={"30px"}
                                alignItems={"center"}
                                backgroundColor={"white"}
                                rounded={"full"}
                                shadow={9}
                              >
                                <HStack>
                                  <Image
                                    source={{ uri: settings.broken }}
                                    alt="sad"
                                    size="18"
                                  />
                                  <Text fontWeight={"bold"} fontSize={"11"}>
                                    {
                                      item?.impressions?.filter(
                                        (x) => x?.impression == "broken",
                                      ).length
                                    }
                                  </Text>
                                </HStack>
                              </Box>
                            )}
                        </HStack>
                      </VStack>

                      {/* Translate and More Options */}
                      {!isOwnChat && shouldTranslate ? (
                        <Box position="relative" zIndex={9999}>
                          <HStack
                            position="absolute"
                            top="10px"
                            right="-60px"
                            alignItems="center"
                            space={1}
                            zIndex={9999}
                          >
                            {/* Translate Icon with Tooltip wrapped together */}
                            <Pressable
                              onHoverIn={() => setIsHovered(true)}
                              onHoverOut={() => setIsHovered(false)}
                              onPress={() => {
                                if (!isTranslated) {
                                  handleTranslateText(item);
                                } else {
                                  handleShowOriginalText(item);
                                }
                                setreplyValue();
                                setIsOpen(false);
                              }}
                              _hover={{
                                bg: "AVColor.hovercolor2",
                                rounded: "full",
                              }}
                              p={2}
                              position="relative"
                            >
                              {/* Tooltip */}
                              {isHovered && (
                                <Box
                                  position="absolute"
                                  top="-30px"
                                  left="50%"
                                  bg="gray.300"
                                  color="white"
                                  px={2}
                                  py={1}
                                  borderRadius="md"
                                  zIndex={10000}
                                  width={!isTranslated ? "120px" : "140px"}
                                  style={{
                                    transform: [{ translateX: -75 }],
                                  }}
                                >
                                  {!isTranslated
                                    ? Loc.currentLang.menu.translateText
                                    : Loc.currentLang.menu.showOriginalTxt}
                                </Box>
                              )}

                              <TranslationIcon
                                width="20px"
                                height="20px"
                                color="#000000"
                              />
                            </Pressable>

                            {/* More Icon */}
                            {item?.id && (
                              <Pressable
                                onPress={() => {
                                  setIsOpen(!isOpen);
                                  setSelectItem(item);
                                }}
                                p={2}
                              >
                                <MoreHorizontalIcon
                                  height="4px"
                                  width="16px"
                                  size="md"
                                  color="gray.400"
                                  style={{ transform: [{ rotate: "90deg" }] }}
                                />
                              </Pressable>
                            )}
                          </HStack>
                        </Box>
                      ) : (
                        item?.id && (
                          <Box position="relative" zIndex={9999}>
                            <Pressable
                              position="absolute"
                              top="10px"
                              right="-10px"
                              onPress={() => {
                                setIsOpen(!isOpen);
                                setSelectItem(item);
                              }}
                              p={2}
                            >
                              <MoreHorizontalIcon
                                height="4px"
                                width="16px"
                                size="md"
                                color="gray.400"
                                style={{ transform: [{ rotate: "90deg" }] }}
                              />
                            </Pressable>
                          </Box>
                        )
                      )}
                    </>
                  )}

                {/* Media with Text */}
                {((item?.media && item?.media?.id?.trim()?.length > 0) ||
                  (item?.galleryPost && item?.galleryPost?.length > 0)) && (
                  <>
                    <VStack
                      w={text?.trim?.() !== "" ? "fit-content" : "260px"}
                      maxW={"500px"}
                      marginTop={"10px"}
                      //width={"260px"}
                      alignSelf={"flex-start"}
                      borderBottomRightRadius={"10px"}
                      borderBottomLeftRadius={"10px"}
                      background={"#00000007"}
                      borderTopRightRadius={"10px"}
                      borderTopLeftRadius={"10px"}
                      marginRight={"15px"}
                      marginBottom={"19px"}
                      shadow={10}
                      padding={"4px"}
                    >
                      {/* Title */}
                      <HStack
                        position={"relative"}
                        space={1}
                        width={"200px"}
                        zIndex={0}
                      >
                        <Text
                          fontSize={"15px"}
                          width={"100%"}
                          isTruncated
                          color={"black"}
                          fontWeight={"600"}
                        >
                          {(profile as ILink)?.title ||
                            (item?.person as AV.ILink)?.title}
                        </Text>
                      </HStack>

                      {/* Text */}
                      {item?.ptype === "H" ? (
                        (() => {
                          if (text.includes("background-image")) {
                            text = text.replace(
                              /<div([^>]*)style="([^"]*)"/,
                              (match, p1, p2) => {
                                if (
                                  /background\s*:\s*(none|unset)/i.test(p2) ||
                                  /background-image\s*:\s*(none|unset)/i.test(
                                    p2,
                                  )
                                ) {
                                  return match;
                                }

                                const hasHeight = /height\s*:/.test(p2);
                                const hasWidth = /width\s*:/.test(p2);

                                let newStyle = p2;
                                if (!hasHeight) newStyle += "; height: 480px";
                                if (!hasWidth) newStyle += "; width: 480px";

                                return `<div${p1} style="${newStyle}"`;
                              },
                            );
                          }

                          let divWidth = "100%";
                          let divHeight = "auto";
                          text = text.replace(
                            /<figure[^>]*>(.*?)<\/figure>/g,
                            "$1",
                          );
                          const htmlPostbgImg = text
                            ? /background-image\s*:\s*(?!\s*(none|unset)\b)[^;]+/i.test(
                                text,
                              )
                            : false;
                          const divStyleMatch = text.match(
                            /<div[^>]*style="[^"]*width\s*:\s*(\d+)(?:px)?;[^"]*height\s*:\s*(\d+)(?:px)?;?[^"]*"/i,
                          );

                          if (divStyleMatch) {
                            divWidth = `${divStyleMatch[1]}px`;
                            divHeight = `${divStyleMatch[2]}px`;
                          }

                          return (
                            <div
                              className="htmlPost"
                              title="post"
                              style={{
                                width: htmlPostbgImg ? divWidth : "100%",
                                minWidth: htmlPostbgImg ? divWidth : "100%",
                                outline: "none",
                                userSelect: "text",
                                cursor: "default",
                              }}
                              contentEditable={htmlPostbgImg}
                              suppressContentEditableWarning={true}
                              onInput={(e) => e.preventDefault()}
                              onKeyDown={(e) => e.preventDefault()}
                              onPaste={(e) => e.preventDefault()}
                              onFocus={(e) => (e.target as HTMLElement).blur()}
                              dangerouslySetInnerHTML={{ __html: text }}
                            />
                          );
                        })()
                      ) : (
                        <VStack
                          paddingBottom={"8px"}
                          width={"100%"}
                          paddingTop={"2px"}
                          position={"relative"}
                          zIndex={0}
                        >
                          <Text
                            width={text?.trim?.() === "" ? "100%" : "unset"}
                            color={"black"}
                            fontSize={"14px"}
                            fontWeight={"400"}
                            lineHeight={"20px"}
                            numberOfLines={
                              text?.trim?.() !== "" ? 0 : undefined
                            }
                            ellipsizeMode={
                              text?.trim?.() !== "" ? "clip" : undefined
                            }
                          >
                            {text}
                          </Text>
                        </VStack>
                      )}

                      {/* Media */}
                      {item?.media?.type?.startsWith("audio") ? (
                        <VoiceMessage
                          audioUrl={Media.getURL(item?.media)}
                          onRecordingComplete={() => {}}
                        />
                      ) : (
                        <Pressable onPress={() => openPreview(item)}>
                          <GridView
                            item={item}
                            setVideoStatus={setVideoStatus}
                            setVideoItem={setVideoItem}
                          />
                        </Pressable>
                      )}

                      {/* Time */}
                      <HStack bottom={"-4px"}>
                        <Text color={"gray.700"} fontSize={"12px"}>
                          {item?.createDate
                            ? Moment(item.createDate).format("hh:mm A")
                            : ""}
                        </Text>
                      </HStack>

                      {/* Received Message Impression of Post*/}
                      <HStack position={"absolute"} bottom={"-14px"} right={0}>
                        {item?.impressions &&
                          item?.impressions?.filter(
                            (x) => x?.impression == "Wow",
                          ).length > 0 && (
                            <Box
                              height={"20px"}
                              width={"30px"}
                              alignItems={"center"}
                              backgroundColor={"white"}
                              rounded={"full"}
                              shadow={9}
                            >
                              <HStack>
                                <Image
                                  source={{ uri: settings.wow }}
                                  alt="startruck"
                                  size="18"
                                />
                                <Text fontSize={11} fontWeight={"bold"}>
                                  {
                                    item?.impressions?.filter(
                                      (x) => x?.impression == "Wow",
                                    ).length
                                  }
                                </Text>
                              </HStack>
                            </Box>
                          )}

                        {item?.impressions &&
                          item?.impressions?.filter(
                            (x) => x?.impression == "Lol",
                          ).length > 0 && (
                            <Box
                              height={"20px"}
                              width={"30px"}
                              alignItems={"center"}
                              backgroundColor={"white"}
                              rounded={"full"}
                              shadow={9}
                            >
                              <HStack>
                                <Image
                                  source={{ uri: settings.lol }}
                                  alt="smile"
                                  size="18"
                                />
                                <Text fontSize={11} fontWeight={"bold"}>
                                  {
                                    item?.impressions?.filter(
                                      (x) => x?.impression == "Lol",
                                    ).length
                                  }
                                </Text>
                              </HStack>
                            </Box>
                          )}

                        {item?.impressions &&
                          item?.impressions?.filter(
                            (x) => x?.impression == "Like",
                          ).length > 0 && (
                            <Box
                              height={"20px"}
                              width={"30px"}
                              alignItems={"center"}
                              backgroundColor={"white"}
                              rounded={"full"}
                              shadow={9}
                            >
                              <HStack>
                                <Image
                                  source={{ uri: settings.like }}
                                  alt="Like"
                                  size="18"
                                />
                                <Text fontWeight={"bold"} fontSize={"11"}>
                                  {
                                    item?.impressions?.filter(
                                      (x) => x?.impression == "Like",
                                    ).length
                                  }
                                </Text>
                              </HStack>
                            </Box>
                          )}

                        {item?.impressions &&
                          item.impressions.filter(
                            (x) => x?.impression == "Unlike",
                          ).length > 0 && (
                            <Box
                              height={"20px"}
                              width={"30px"}
                              alignItems={"center"}
                              backgroundColor={"white"}
                              rounded={"full"}
                              shadow={9}
                            >
                              <HStack>
                                <Image
                                  source={{ uri: settings.unlike }}
                                  alt="Unlike"
                                  size="18"
                                />
                                <Text fontWeight={"bold"} fontSize={"11"}>
                                  {
                                    item.impressions.filter(
                                      (x) => x?.impression == "Unlike",
                                    ).length
                                  }
                                </Text>
                              </HStack>
                            </Box>
                          )}

                        {item?.impressions &&
                          item?.impressions?.filter(
                            (x) => x?.impression == "Cheer",
                          ).length > 0 && (
                            <Box
                              height={"20px"}
                              width={"30px"}
                              alignItems={"center"}
                              backgroundColor={"white"}
                              rounded={"full"}
                              shadow={9}
                            >
                              <HStack>
                                <Image
                                  source={{ uri: settings.cheer }}
                                  alt="sad"
                                  size="18"
                                />
                                <Text fontWeight={"bold"} fontSize={"11"}>
                                  {
                                    item?.impressions?.filter(
                                      (x) => x?.impression == "Cheer",
                                    ).length
                                  }
                                </Text>
                              </HStack>
                            </Box>
                          )}

                        {item?.impressions &&
                          item?.impressions?.filter(
                            (x) => x?.impression == "Question",
                          ).length > 0 && (
                            <Box
                              height={"20px"}
                              width={"30px"}
                              alignItems={"center"}
                              backgroundColor={"white"}
                              rounded={"full"}
                              shadow={9}
                            >
                              <HStack>
                                <Image
                                  source={{ uri: settings.question }}
                                  alt="stars"
                                  size="18"
                                />
                                <Text fontWeight={"bold"} fontSize={"11"}>
                                  {
                                    item?.impressions?.filter(
                                      (x) => x?.impression == "Question",
                                    ).length
                                  }
                                </Text>
                              </HStack>
                            </Box>
                          )}

                        {item?.impressions &&
                          item?.impressions?.filter(
                            (x) => x?.impression == "Sad",
                          ).length > 0 && (
                            <Box
                              height={"20px"}
                              width={"30px"}
                              alignItems={"center"}
                              backgroundColor={"white"}
                              rounded={"full"}
                              shadow={9}
                            >
                              <HStack>
                                <Image
                                  source={{ uri: settings.sad }}
                                  alt="sads"
                                  size="18"
                                />
                                <Text fontWeight={"bold"} fontSize={"11"}>
                                  {
                                    item?.impressions?.filter(
                                      (x) => x?.impression == "Sad",
                                    ).length
                                  }
                                </Text>
                              </HStack>
                            </Box>
                          )}

                        {item?.impressions &&
                          item?.impressions?.filter(
                            (x) => x?.impression == "Mad",
                          ).length > 0 && (
                            <Box
                              height={"20px"}
                              width={"30px"}
                              alignItems={"center"}
                              backgroundColor={"white"}
                              rounded={"full"}
                              shadow={9}
                            >
                              <HStack>
                                <Image
                                  source={{ uri: settings.mad }}
                                  alt="sad"
                                  size="18"
                                />
                                <Text fontWeight={"bold"} fontSize={"11"}>
                                  {
                                    item?.impressions?.filter(
                                      (x) => x?.impression == "Mad",
                                    ).length
                                  }
                                </Text>
                              </HStack>
                            </Box>
                          )}

                        {item?.impressions &&
                          item?.impressions?.filter(
                            (x) => x?.impression == "heart",
                          ).length > 0 && (
                            <Box
                              height={"20px"}
                              width={"30px"}
                              alignItems={"center"}
                              backgroundColor={"white"}
                              rounded={"full"}
                              shadow={9}
                            >
                              <HStack>
                                <Image
                                  source={{ uri: settings.heart }}
                                  alt="heart"
                                  size="18"
                                />
                                <Text fontWeight={"bold"} fontSize={"11"}>
                                  {
                                    item?.impressions?.filter(
                                      (x) => x?.impression == "heart",
                                    ).length
                                  }
                                </Text>
                              </HStack>
                            </Box>
                          )}

                        {item?.impressions &&
                          item?.impressions?.filter(
                            (x) => x?.impression == "broken",
                          ).length > 0 && (
                            <Box
                              height={"20px"}
                              width={"30px"}
                              alignItems={"center"}
                              backgroundColor={"white"}
                              rounded={"full"}
                              shadow={9}
                            >
                              <HStack>
                                <Image
                                  source={{ uri: settings.broken }}
                                  alt="sad"
                                  size="18"
                                />
                                <Text fontWeight={"bold"} fontSize={"11"}>
                                  {
                                    item?.impressions?.filter(
                                      (x) => x?.impression == "broken",
                                    ).length
                                  }
                                </Text>
                              </HStack>
                            </Box>
                          )}
                      </HStack>
                    </VStack>

                    {/* More Options */}
                    {item?.id && (
                      <Pressable
                        top={"10px"}
                        left={"-25px"}
                        onPress={() => {
                          setIsOpen(true);
                          setSelectItem(item);
                        }}
                      >
                        <MoreHorizontalIcon
                          height={"4px"}
                          width={"16px"}
                          size={"md"}
                          color={"gray.400"}
                          style={{ transform: [{ rotate: "90deg" }] }}
                        />
                      </Pressable>
                    )}
                  </>
                )}
              </HStack>
            </VStack>
          </View>
        )}
      </>
    );
  },
);

export default React.memo(ConvoCard);
